import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { useState, useRef, useEffect } from 'react';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Spacing,
  useViewport,
} from '@farmersdog/corgi-x';
import { Check } from '@farmersdog/corgi-x/icons';

import { trackRecipesClickHowItWorks } from '../../../../analytics/events';
import { GettingStartedBadges } from '../GettingStartedBadges/GettingStartedBadges';

import styles from './PopularPlanOptions.module.css';

const RESIZE_LISTENER_DEBOUNCE_MS = 100;

/** "Popular Plan Options" card in Recipes Page: Getting Started */
export function PopularPlanOptions({
  preselectedDailyPrice,
  otherOptionDailyPrice,
  preselectedDaysOfFood,
  otherOptionDaysOfFood,
}: {
  preselectedDailyPrice: string;
  otherOptionDailyPrice: string;
  preselectedDaysOfFood: number;
  otherOptionDaysOfFood: number;
}) {
  const isDesktop = useViewport().lg;
  const [isOptionFlipped, setIsOptionFlipped] = useState(false);
  const [bulkDiscountDimensions, setBulkDiscountDimensions] = useState({
    width: 0,
    height: 0,
  });
  const handleOnClick = () => {
    trackRecipesClickHowItWorks();
    setIsOptionFlipped(!isOptionFlipped);
    setTimeout(() => {
      setIsOptionFlipped(false);
    }, 5000);
  };
  const preselectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateDimensions = () => {
      if (preselectedRef.current) {
        const boundingClientRect =
          preselectedRef.current.getBoundingClientRect();
        setBulkDiscountDimensions({
          width: boundingClientRect.width,
          height: boundingClientRect.height,
        });
      }
    };

    const debouncedUpdateDimensions = debounce(
      updateDimensions,
      RESIZE_LISTENER_DEBOUNCE_MS
    );

    updateDimensions();
    window.addEventListener('resize', debouncedUpdateDimensions);

    return () => {
      window.removeEventListener('resize', debouncedUpdateDimensions);
    };
  }, []);

  return (
    <Box
      p={{ xs: 'none', lg: 'md' }}
      borderRadius="md"
      className={styles.popularPlanOptionsWrapper}
    >
      <Text
        variant="heading-22"
        color="Kale3"
        bold
        align="center"
        as="h3"
        bottomSpacing="sm"
      >
        Popular Plan Options
      </Text>
      <Grid flexDirection={{ xs: 'row', lg: 'column' }} gap="sm">
        <GridItem
          className={classNames(styles.preselected, styles.optionCard)}
          sm={6}
          lg={12}
        >
          <div ref={preselectedRef}>
            <Flex
              direction="column"
              gap="xs"
              justify="center"
              alignItems="center"
            >
              <Text color="Charcoal3" variant="heading-22" bold align="center">
                {isDesktop ? (
                  <>{preselectedDaysOfFood} Days of Food</>
                ) : (
                  <>
                    {preselectedDaysOfFood} Days
                    <br />
                    of Food
                  </>
                )}
              </Text>
              <Text
                color="Charcoal3"
                variant="heading-16"
                bold
                bottomSpacing={{
                  xs: 'md',
                  lg: 'xs',
                }}
              >
                {preselectedDailyPrice}/day
              </Text>
              <Flex direction="column" gap="sm" alignItems="center">
                <Flex direction="row" gap="xs" alignItems="center">
                  <Check width={13} height={12} baseColor="Carrot2" />
                  <Text bold variant="heading-12">
                    Preselected
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </div>
        </GridItem>
        <GridItem
          sm={6}
          lg={12}
          style={{
            width: bulkDiscountDimensions.width + Spacing.XL,
            height: bulkDiscountDimensions.height + Spacing.XL,
          }}
        >
          <Box className={classNames(styles.animationWrapper)}>
            <Box
              backgroundColor="White"
              borderColor="Oyster2"
              onClick={handleOnClick}
              borderRadius="md"
              p="md"
              className={classNames(
                styles.additionalOption,
                isOptionFlipped ? styles.flipped : styles.notFlipped
              )}
            >
              <Flex
                alignItems="center"
                className={classNames(styles.afterCheckout)}
                style={{
                  width: bulkDiscountDimensions.width,
                  height: bulkDiscountDimensions.height,
                }}
              >
                <Text variant="heading-16" color="kale-2" align="center" as="p">
                  Available right after checkout in your account
                </Text>
              </Flex>
              <Box
                className={classNames(styles.bulkDiscount)}
                style={{
                  width: bulkDiscountDimensions.width,
                  height: bulkDiscountDimensions.height,
                }}
              >
                <Flex
                  direction="column"
                  gap="xs"
                  justify="center"
                  alignItems="center"
                >
                  <Text
                    color="Charcoal3"
                    variant="heading-22"
                    bold
                    align="center"
                  >
                    {isDesktop ? (
                      <>{otherOptionDaysOfFood} Days of Food</>
                    ) : (
                      <>
                        {otherOptionDaysOfFood} Days <br />
                        of Food
                      </>
                    )}
                  </Text>
                  <Text
                    color="Charcoal3"
                    variant="heading-16"
                    bold
                    align="center"
                    bottomSpacing={{
                      xs: 'md',
                      lg: 'xs',
                    }}
                  >
                    {otherOptionDailyPrice}/day
                  </Text>
                  <GettingStartedBadges badges={['Bulk Discount']} />
                </Flex>
              </Box>
            </Box>
          </Box>
        </GridItem>

        <GridItem xs={12}>
          <Text
            as="h5"
            vSpacing="none"
            align="center"
            variant="heading-16"
            color="charcoal-3"
          >
            Bulk discount pricing and other plans available after checkout via
            account or 24/7 CX support.
          </Text>
        </GridItem>
      </Grid>
    </Box>
  );
}
